/**
 * Système de rappels : polling get_active_reminders, contexte, affichage in-app.
 * - Plus de modale : affichage inline sur le tableau de bord (notification à gauche du bouton EDIT).
 * - Son + liste des rappels dans la page (pas de permission navigateur).
 * - Plusieurs rappels : tous sont exposés via le contexte et affichés dans la liste.
 */

import { useAuth } from '@/contexts/AuthContext';
import { useReminderContext, type ReminderItem } from '@/contexts/ReminderContext';
import { supabase } from '@/services/supabaseClient';
import React, { useEffect, useRef } from 'react';

const POLL_INTERVAL_MS = 10000;

function playReminderSound() {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const playNote = (freq: number, start: number, dur: number, vol = 0.25) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.connect(gain);
      gain.connect(audioContext.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(vol, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.01, start + dur);
      osc.start(start);
      osc.stop(start + dur);
    };
    const now = audioContext.currentTime;
    playNote(523.25, now, 0.15);
    playNote(659.25, now + 0.12, 0.15);
    playNote(783.99, now + 0.24, 0.3);
  } catch (_) {}
}

function showBrowserNotification(reminder: ReminderItem) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const title = reminder.item_type === 'appointment' ? 'Rappel de rendez-vous' : 'Rappel de tâche';
  const date = new Date(reminder.reminder_datetime);
  const body = `${reminder.title} - ${date.toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
  try {
    new Notification(title, { body, icon: '/favicon.ico' });
  } catch (_) {}
}

const ReminderSystem: React.FC = () => {
  const { user } = useAuth();
  const { setActiveReminders, registerRefresh } = useReminderContext();
  const notifiedIdsRef = useRef<Set<string>>(new Set());
  const previousCountRef = useRef(0);

  useEffect(() => {
    const checkReminders = async () => {
      if (!user?.email) return;
      try {
        const { data, error } = await supabase.rpc('get_active_reminders', {
          p_user_email: user.email,
        });
        if (error) {
          console.error('Erreur RPC get_active_reminders:', error);
          return;
        }
        const reminders = (data || []) as ReminderItem[];

        // Ne jamais vider la liste côté API : on n'ajoute que les nouveaux rappels.
        // Un rappel ne disparaît que si l'utilisateur clique Fait / Snooze / Supprimer.
        setActiveReminders((prev: ReminderItem[]) => {
          if (reminders.length === 0) return prev;
          const existingIds = new Set(prev.map((p: ReminderItem) => p.id));
          const newOnes = reminders.filter(r => !existingIds.has(r.id));
          const hasNew = newOnes.length > 0;
          const firstTimeWithReminders = prev.length === 0 && reminders.length > 0;
          if (hasNew || firstTimeWithReminders) {
            playReminderSound();
          }
          previousCountRef.current = hasNew ? prev.length + newOnes.length : prev.length;
          return hasNew ? [...prev, ...newOnes] : prev;
        });

        // Notifications navigateur quand l'onglet est en arrière-plan
        if (document.visibilityState === 'hidden' && reminders.length > 0) {
          reminders.forEach(r => {
            if (!notifiedIdsRef.current.has(r.id)) {
              notifiedIdsRef.current.add(r.id);
              showBrowserNotification(r);
            }
          });
        }
      } catch (err) {
        console.error('Erreur vérification rappels:', err);
      }
    };

    // Vider l'état au changement d'utilisateur (logout / switch de compte)
    // pour éviter qu'un rappel d'une session précédente reste affiché.
    setActiveReminders([]);
    notifiedIdsRef.current.clear();

    registerRefresh(checkReminders);
    checkReminders();
    const interval = setInterval(checkReminders, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user?.email, setActiveReminders, registerRefresh]);

  // Réinitialiser les IDs notifiés quand l'utilisateur revient sur l'onglet
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        notifiedIdsRef.current.clear();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  return null;
};

export default ReminderSystem;
